/**
 * Shared Omnidirectional 360° Barcode Scanner Hook & Engine
 * 
 * Supports instant hardware-accelerated scanning across all orientations:
 * - 0° (standard horizontal)
 * - 90° & 270° (vertical, bottles, cans)
 * - 180° (upside-down)
 * - 45° & diagonal tilted angles
 * 
 * Uses Native BarcodeDetector (iOS 17+ / Chrome Android) with ZXing TRY_HARDER fallback.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { BrowserMultiFormatReader, IScannerControls } from '@zxing/browser';
import { DecodeHintType } from '@zxing/library';
import { compressImage } from './imageUtils';

export interface UseBarcodeScannerOptions {
  onDetected: (barcode: string) => void;
  enabled?: boolean;
}

export interface UseBarcodeScannerReturn {
  videoRef: React.RefObject<HTMLVideoElement>;
  isScanning: boolean;
  isCameraActive: boolean;
  cameraError: string | null;
  torchAvailable: boolean;
  isTorchOn: boolean;
  startCamera: () => Promise<void>;
  startScanningVideo: (video: HTMLVideoElement) => void;
  stopCamera: () => void;
  toggleTorch: () => Promise<void>;
  decodeFromPhoto: (file: File) => Promise<string | null>;
}

export function useBarcodeScanner({
  onDetected,
  enabled = true,
}: UseBarcodeScannerOptions): UseBarcodeScannerReturn {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const scannerControlsRef = useRef<IScannerControls | null>(null);
  const isScanningRef = useRef<boolean>(false);
  const isStartingRef = useRef<boolean>(false);
  const animFrameRef = useRef<number | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const onDetectedRef = useRef(onDetected);
  onDetectedRef.current = onDetected;

  const [isCameraActive, setIsCameraActive] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [torchAvailable, setTorchAvailable] = useState(false);
  const [isTorchOn, setIsTorchOn] = useState(false);

  // Stop camera and release all hardware resources
  const stopCamera = useCallback(() => {
    isScanningRef.current = false;
    isStartingRef.current = false;
    setIsScanning(false);

    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }

    if (scannerControlsRef.current) {
      try {
        scannerControlsRef.current.stop();
      } catch {}
      scannerControlsRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch {}
      });
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setIsCameraActive(false);
    setIsTorchOn(false);
  }, []);

  // Handle successful barcode lock-on
  const handleSuccess = useCallback((code: string) => {
    const clean = (code || '').trim();
    if (!clean || !isScanningRef.current) return;

    isScanningRef.current = false;
    setIsScanning(false);

    // Haptic confirmation vibration
    if ('vibrate' in navigator) {
      try {
        navigator.vibrate(100);
      } catch {}
    }

    onDetectedRef.current(clean);
  }, []);

  // Run the omnidirectional multi-engine scanning loop on an active HTMLVideoElement
  const startScanningVideo = useCallback(
    async (video: HTMLVideoElement) => {
      if (!video) return;

      isScanningRef.current = true;
      setIsScanning(true);

      // Clean up previous scan frame loop if running
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
        animFrameRef.current = null;
      }
      if (scannerControlsRef.current) {
        try {
          scannerControlsRef.current.stop();
        } catch {}
        scannerControlsRef.current = null;
      }

      // 1. Check for Hardware Native BarcodeDetector (iOS 17+, Chrome Android)
      let nativeDetector: any = null;
      if ('BarcodeDetector' in window) {
        try {
          const supported: string[] = await (window as any).BarcodeDetector.getSupportedFormats();
          const targetFormats = [
            'ean_13',
            'ean_8',
            'upc_a',
            'upc_e',
            'code_128',
            'code_39',
            'qr_code',
          ].filter((fmt) => supported.includes(fmt));

          if (targetFormats.length > 0) {
            nativeDetector = new (window as any).BarcodeDetector({ formats: targetFormats });
          }
        } catch (e) {
          nativeDetector = null;
        }
      }

      if (nativeDetector) {
        let frameCount = 0;
        const scanFrame = async () => {
          if (!isScanningRef.current) return;

          try {
            if (video.readyState >= 2 && video.videoWidth > 0) {
              frameCount++;

              // Pass A: Upright frame (0° / horizontal)
              let barcodes = await nativeDetector.detect(video);

              // Pass B: If not detected, check 90° rotated canvas buffer (vertical / tall containers)
              if ((!barcodes || barcodes.length === 0) && frameCount % 2 === 0) {
                if (!canvasRef.current) {
                  canvasRef.current = document.createElement('canvas');
                }
                const canvas = canvasRef.current;
                const ctx = canvas.getContext('2d', { willReadFrequently: true });
                if (ctx) {
                  const vw = video.videoWidth;
                  const vh = video.videoHeight;
                  canvas.width = vh;
                  canvas.height = vw;
                  ctx.save();
                  ctx.translate(canvas.width / 2, canvas.height / 2);
                  ctx.rotate(Math.PI / 2);
                  ctx.drawImage(video, -vw / 2, -vh / 2);
                  ctx.restore();
                  barcodes = await nativeDetector.detect(canvas);
                }
              }

              // Pass C: If still not detected, check 45° diagonal angle (every 4th frame)
              if ((!barcodes || barcodes.length === 0) && frameCount % 4 === 0) {
                if (!canvasRef.current) {
                  canvasRef.current = document.createElement('canvas');
                }
                const canvas = canvasRef.current;
                const ctx = canvas.getContext('2d', { willReadFrequently: true });
                if (ctx) {
                  const vw = video.videoWidth;
                  const vh = video.videoHeight;
                  canvas.width = Math.min(vw, vh);
                  canvas.height = Math.min(vw, vh);
                  ctx.save();
                  ctx.translate(canvas.width / 2, canvas.height / 2);
                  ctx.rotate(Math.PI / 4);
                  ctx.drawImage(video, -vw / 2, -vh / 2);
                  ctx.restore();
                  barcodes = await nativeDetector.detect(canvas);
                }
              }

              if (barcodes && barcodes.length > 0) {
                const val = barcodes[0].rawValue;
                if (val) {
                  handleSuccess(val);
                  return;
                }
              }
            }
          } catch {}

          if (isScanningRef.current) {
            animFrameRef.current = requestAnimationFrame(scanFrame);
          }
        };

        animFrameRef.current = requestAnimationFrame(scanFrame);
        return;
      }

      // 2. ZXing BrowserMultiFormatReader Fallback (with TRY_HARDER)
      try {
        const hints = new Map();
        hints.set(DecodeHintType.TRY_HARDER, true);
        const reader = new BrowserMultiFormatReader(hints);

        reader
          .decodeFromVideoElement(video, (result) => {
            if (result && isScanningRef.current) {
              const text = result.getText();
              if (text) {
                handleSuccess(text);
              }
            }
          })
          .then((controls) => {
            scannerControlsRef.current = controls;
          })
          .catch((err) => {
            console.warn('ZXing video decoder error:', err);
          });
      } catch (err) {
        console.warn('ZXing initialization error:', err);
      }
    },
    [handleSuccess]
  );

  // Start rear-facing camera stream with omnidirectional frame loop (standalone fallback)
  const startCamera = useCallback(async () => {
    if (isStartingRef.current || isScanningRef.current) return;
    isStartingRef.current = true;
    setCameraError(null);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
        },
        audio: false,
      });

      streamRef.current = stream;

      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        const capabilities: any = videoTrack.getCapabilities ? videoTrack.getCapabilities() : {};
        setTorchAvailable(Boolean(capabilities && capabilities.torch));
      }

      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        video.muted = true;
        video.playsInline = true;
        video.setAttribute('playsinline', 'true');
        video.setAttribute('webkit-playsinline', 'true');

        try {
          await video.play();
        } catch (playErr) {
          console.warn('Standalone scanner video play error:', playErr);
        }

        setIsCameraActive(true);
        startScanningVideo(video);
      }
    } catch (err: any) {
      console.warn('Camera access error in useBarcodeScanner:', err);
      setIsCameraActive(false);
      setIsScanning(false);
      isScanningRef.current = false;
      setCameraError(
        err?.name === 'NotAllowedError'
          ? 'Camera permission denied. Please enable camera access in Settings.'
          : 'Could not connect to camera hardware. Please enter digits manually.'
      );
    } finally {
      isStartingRef.current = false;
    }
  }, [startScanningVideo]);

  // Toggle Torch / Flashlight
  const toggleTorch = useCallback(async () => {
    if (!streamRef.current) return;
    const videoTrack = streamRef.current.getVideoTracks()[0];
    if (!videoTrack) return;

    try {
      const next = !isTorchOn;
      await (videoTrack as any).applyConstraints({
        advanced: [{ torch: next }],
      });
      setIsTorchOn(next);
    } catch (e) {
      console.warn('Failed to toggle camera torch:', e);
    }
  }, [isTorchOn]);

  // Decode barcode from an uploaded image/photo
  const decodeFromPhoto = useCallback(async (file: File): Promise<string | null> => {
    if (!file) return null;

    try {
      const compressed = await compressImage(file, 1200, 0.88);
      const img = new Image();
      img.src = compressed;
      await new Promise((resolve) => {
        img.onload = resolve;
        img.onerror = resolve;
      });

      // 1. Try Native BarcodeDetector on image
      if ('BarcodeDetector' in window) {
        try {
          const supported: string[] = await (window as any).BarcodeDetector.getSupportedFormats();
          const targetFormats = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'qr_code'].filter(
            (f) => supported.includes(f)
          );
          if (targetFormats.length > 0) {
            const detector = new (window as any).BarcodeDetector({ formats: targetFormats });
            const barcodes = await detector.detect(img);
            if (barcodes && barcodes.length > 0 && barcodes[0].rawValue) {
              return barcodes[0].rawValue;
            }
          }
        } catch {}
      }

      // 2. Try ZXing with standard and 90-degree rotated canvas
      const hints = new Map();
      hints.set(DecodeHintType.TRY_HARDER, true);
      const reader = new BrowserMultiFormatReader(hints);

      try {
        const result = await reader.decodeFromImageElement(img);
        if (result && result.getText()) {
          return result.getText();
        }
      } catch {}

      // Rotate image 90 degrees and retry ZXing
      try {
        const rotCanvas = document.createElement('canvas');
        rotCanvas.width = img.naturalHeight || img.height;
        rotCanvas.height = img.naturalWidth || img.width;
        const ctx = rotCanvas.getContext('2d');
        if (ctx) {
          ctx.translate(rotCanvas.width / 2, rotCanvas.height / 2);
          ctx.rotate(Math.PI / 2);
          ctx.drawImage(img, -img.width / 2, -img.height / 2);
          const rotResult = await reader.decodeFromCanvas(rotCanvas);
          if (rotResult && rotResult.getText()) {
            return rotResult.getText();
          }
        }
      } catch {}

      return null;
    } catch (err) {
      console.warn('Barcode photo decode error:', err);
      return null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  return {
    videoRef,
    isScanning,
    isCameraActive,
    cameraError,
    torchAvailable,
    isTorchOn,
    startCamera,
    startScanningVideo,
    stopCamera,
    toggleTorch,
    decodeFromPhoto,
  };
}
